'use client';

/**
 * DomainTransferPage.tsx — /domener/flytt («Overfør domene»)
 *
 * Hero med «Be om flytting»-skjema (kun domenenavn; overføringskoden sendes aldri på
 * e-post), oversikt, fire steg, sjekkliste før flytting, flyttepriser for populære
 * endelser, promo til søk, FAQ og avsluttende CTA.
 * Innhold: domain-shared.ts + domain-flytt.ts; priser: domain-tlds.ts.
 */

import ClosingCta from '@/components/landing/ClosingCta';
import DomainHeroIllustration from '@/components/domain/DomainHeroIllustration';
import DomainPanelIllustration from '@/components/domain/DomainPanelIllustration';
import DomainShell from '@/components/domain/DomainShell';
import TldTable from '@/components/domain/TldTable';
import TransferRequest from '@/components/domain/TransferRequest';
import FaqAccordion from '@/components/product/FaqAccordion';
import FeatureAccordion from '@/components/product/FeatureAccordion';
import NumberedSteps from '@/components/product/NumberedSteps';
import ProductHero from '@/components/product/ProductHero';
import PromoBanner from '@/components/product/PromoBanner';
import SectionHeader from '@/components/product/SectionHeader';
import SectionNav from '@/components/product/SectionNav';
import styles from '@/components/product/product.module.css';
import { useLocale } from '@/hooks/useLocale';
import { DOMAIN_FLYTT } from './domain-flytt';
import { DOMAIN_SHARED } from './domain-shared';
import { DOMAIN_POPULAR } from './domain-tlds';

export default function DomainTransferPage() {
  const { locale, setLocale } = useLocale();
  const shared = DOMAIN_SHARED[locale];
  const page = DOMAIN_FLYTT[locale];

  return (
    <DomainShell shared={shared} sectionNav={page.sectionNav} locale={locale} onLocaleChange={setLocale}>
      <ProductHero
        hero={page.hero}
        breadcrumb={page.breadcrumb}
        illustration={<DomainHeroIllustration label={page.hero.illustrationLabel} />}
        extra={<TransferRequest request={page.request} />}
      />
      <SectionNav nav={page.sectionNav} />

      <section id="oversikt" className={styles.section} aria-labelledby="overview-heading">
        <div className={styles.container}>
          <SectionHeader id="overview-heading" eyebrow={page.overview.eyebrow} heading={page.overview.heading} blue>
            {page.overview.body.map((paragraph) => (
              <p key={paragraph} className={styles.lead}>
                {paragraph}
              </p>
            ))}
          </SectionHeader>
        </div>
      </section>

      <NumberedSteps id="steg" steps={page.steps} art={<DomainPanelIllustration label={page.steps.panelLabel} />} />
      <FeatureAccordion
        id="sjekkliste"
        features={page.checklist}
        art={<DomainPanelIllustration label={page.checklist.panelLabel} />}
      />
      <TldTable id="priser" content={page.prices} items={DOMAIN_POPULAR} currency={shared.currency} />
      <PromoBanner promo={page.promo} />
      <FaqAccordion faq={page.faq} />
      <ClosingCta cta={page.cta} />
    </DomainShell>
  );
}
